import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useData } from "../context/DataContext";
import { principalSummary, months, projectRawTotal, projectWeightedTotal } from "../lib/calc";
import { fmtMoney, fmtMonthShort, contractLabel, projectNumber } from "../lib/format";
import { downloadCsv } from "../lib/csv";
import { ArrowRight, Download } from "lucide-react";
import DragScrollContainer from "../components/DragScrollContainer";
import { AllProjectsTable } from "../components/AllProjectsTable";

const VIEWS = [
  { id: "byPrincipal", label: "By Principal" },
  { id: "all", label: "All Projects" },
];

export default function Principals() {
  const { data } = useData();
  const [searchParams, setSearchParams] = useSearchParams();
  const view = searchParams.get("view") === "all" ? "all" : "byPrincipal";

  const setView = (v) => {
    const next = new URLSearchParams(searchParams);
    if (v === "all") next.set("view", "all");
    else { next.delete("view"); next.delete("principal"); }
    setSearchParams(next, { replace: true });
  };

  const openAllForPrincipal = (id) => {
    const next = new URLSearchParams(searchParams);
    next.set("view", "all");
    next.set("principal", id);
    setSearchParams(next);
  };

  return (
    <div className="space-y-8" data-testid="principals-view">
      <div className="flex flex-wrap justify-between items-end gap-4">
        <div>
          <div className="eyebrow">{view === "all" ? "Cross-firm ranking" : "By principal"}</div>
          <h2 className="section-h mt-1">{view === "all" ? "All projects" : "Principal portfolios"}</h2>
        </div>
        <div className="inline-flex border border-[var(--rule-strong)] bg-white" data-testid="principals-view-toggle">
          {VIEWS.map((v) => (
            <button
              key={v.id}
              onClick={() => setView(v.id)}
              className={`px-3 py-1.5 text-[11px] font-display tracked-tight uppercase border-r last:border-r-0 border-[var(--rule-strong)] ${
                view === v.id
                  ? "bg-[var(--ink)] text-white"
                  : "text-[var(--muted-fg)] hover:bg-[var(--paper-soft)]"
              }`}
              data-testid={`principals-view-${v.id === "all" ? "all-projects" : "by-principal"}`}
            >
              {v.label}
            </button>
          ))}
        </div>
      </div>

      {view === "all" ? <AllProjectsTable /> : <ByPrincipal data={data} onOpenAll={openAllForPrincipal} />}
    </div>
  );
}

function ByPrincipal({ data, onOpenAll }) {
  const m = months(data);
  const summary = useMemo(() => principalSummary(data), [data]);

  return (
    <>
      {summary.map((pr) => {
        const maxLoad = Math.max(1, ...Object.values(pr.monthlyLoad));
        const exportPrincipal = () => {
          const header = ["Project", "ID", "Contract", "Stages", "Raw", "Weighted"];
          const body = pr.projects.map((p) => [
            p.name, p.id, p.contract, p.scopes.length, projectRawTotal(p), projectWeightedTotal(p),
          ]);
          downloadCsv(`principal-${pr.id}.csv`, [header, ...body]);
        };
        return (
          <section key={pr.id} className="border border-[var(--rule)]" data-testid={`principal-${pr.id}`}>
            <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-[var(--rule)]">
              <div className="p-6 md:col-span-1">
                <div className="eyebrow">Principal</div>
                <h3 className="font-display text-2xl mt-2">{pr.name}</h3>
                <div className="text-xs font-mono-num text-[var(--soft-fg)] mt-2">{pr.projects.length} project{pr.projects.length!==1?"s":""}</div>
                <div className="flex items-center gap-2 mt-4">
                  <button
                    onClick={() => onOpenAll(pr.id)}
                    className="inline-flex items-center gap-2 px-3 py-1.5 border border-[var(--rule-strong)] text-[11px] font-display tracked-tight uppercase hover:bg-[var(--paper-soft)]"
                    data-testid={`principal-open-${pr.id}`}
                  >
                    View in All Projects <ArrowRight size={12} />
                  </button>
                  <button
                    onClick={exportPrincipal}
                    className="inline-flex items-center gap-2 px-3 py-1.5 border border-[var(--rule)] text-[11px] font-display tracked-tight uppercase hover:bg-[var(--paper-soft)]"
                    data-testid={`principal-csv-${pr.id}`}
                  >
                    <Download size={12} /> CSV
                  </button>
                </div>
              </div>
              <div className="p-6">
                <div className="eyebrow">Raw forecast</div>
                <div className="font-mono-num text-2xl mt-1" data-testid={`principal-raw-${pr.id}`}>{fmtMoney(pr.totalRaw)}</div>
              </div>
              <div className="p-6">
                <div className="eyebrow">Weighted forecast</div>
                <div className="font-mono-num text-2xl mt-1 text-[var(--soft-fg)]" data-testid={`principal-weighted-${pr.id}`}>{fmtMoney(pr.totalWeighted)}</div>
              </div>
            </div>

            {/* Team load mini-bars */}
            <div className="px-6 py-5 border-t border-[var(--rule)]">
              <div className="eyebrow mb-3">Team load generated · FTE per month</div>
              <div className="grid" style={{ gridTemplateColumns: `repeat(${m.length}, minmax(0, 1fr))`, gap: 6 }}>
                {m.map((mo) => {
                  const load = pr.monthlyLoad[mo] || 0;
                  const h = Math.max(4, (load / maxLoad) * 80);
                  return (
                    <div key={mo} className="flex flex-col items-center justify-end" style={{ height: 96 }}>
                      <div title={`${mo} · ${load.toFixed(2)} FTE`} className="w-full bg-[var(--ink)]" style={{ height: h, opacity: 0.85 }} />
                      <div className="eyebrow mt-2">{fmtMonthShort(mo)}</div>
                      <div className="font-mono-num text-[10px] text-[var(--soft-fg)]">{load.toFixed(1)}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Projects table */}
            <DragScrollContainer className="border-t border-[var(--rule)] overflow-x-auto thin-scroll">
              <table className="w-full text-sm">
                <thead>
                  <tr className="eyebrow">
                    <th className="px-6 py-3 text-left">Project</th>
                    <th className="px-4 py-3 text-left">Contract</th>
                    <th className="px-4 py-3 text-right">Stages</th>
                    <th className="px-4 py-3 text-right">Raw</th>
                    <th className="px-4 py-3 text-right">Weighted</th>
                  </tr>
                </thead>
                <tbody>
                  {pr.projects.map((p) => {
                    const chip = p.contract === "Yes" ? "chip-yes" : p.contract === "Tentative" ? "chip-tentative" : "chip-no";
                    return (
                      <tr key={p.id} className="border-t border-[var(--rule)]">
                        <td className="px-6 py-3">
                          <div>{p.name}</div>
                          <div className="font-mono-num text-[10px] text-[var(--soft-fg)] mt-0.5">{projectNumber(p)}</div>
                        </td>
                        <td className="px-4 py-3"><span className={`chip ${chip}`}>{contractLabel(p.contract)}</span></td>
                        <td className="px-4 py-3 text-right font-mono-num">{p.scopes.length}</td>
                        <td className="px-4 py-3 text-right font-mono-num">{fmtMoney(projectRawTotal(p))}</td>
                        <td className="px-4 py-3 text-right font-mono-num text-[var(--soft-fg)]">{fmtMoney(projectWeightedTotal(p))}</td>
                      </tr>
                    );
                  })}
                  {pr.projects.length === 0 && (
                    <tr>
                      <td colSpan={5} className="py-8 text-center">
                        <div className="eyebrow text-[var(--soft-fg)]">No projects under this principal yet</div>
                        <div className="text-xs font-mono-num text-[var(--soft-fg)] mt-2">
                          They can add projects from their portfolio — or an admin can, via View as → {pr.name.split(" ")[0]}.
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </DragScrollContainer>
          </section>
        );
      })}
    </>
  );
}
